// Advertise widget: data-365="advertise"
// Lets a local business advertise on THIS venue's screens, natively from the venue's own site.
// Browse-only here (shows the venue's ad menu + prices from the embed config); "Create your ad"
// full-page-redirects to the first-party 365 create-ad flow pre-scoped to this venue (login +
// creative upload + submit all happen first-party, per the embed's redirect model), then returns
// here with ?advertise=submitted. Mirrors the booking widget's redirect pattern.
// A signed-out visitor goes through the embed login FIRST: that one login signs them in here AND on
// 365 (embed-login persists the platform session), so when they come back the My Ads widget can show
// and manage the ad they just submitted. Going straight to /create-ad left this site signed out.
import { Api } from '../api';
import { Auth } from '../auth';
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, footer, clear, showSalesPaused, blockIfSalesPaused, salesPausedMessage } from '../ui';
import { sitePage, hasMount } from '../pages';
import { track, withWa } from '../analytics';

function platformBase(cfg: EmbedConfig): string {
  return (cfg.loginUrl || '').replace(/\/embed-login\.html.*$/, '').replace(/\/$/, '');
}

interface AdFormat { label: string; blurb: string; price?: number | null; }

function formats(cfg: EmbedConfig): AdFormat[] {
  const p = cfg.adPrices || {};
  return [
    { label: 'Image ad', blurb: 'A full-screen banner or flyer.', price: p.staticImage },
    { label: 'Info card', blurb: 'Headline + description + your logo.', price: p.richCard },
    { label: 'Video ad', blurb: 'A 15–30 second video spot.', price: p.video },
    { label: 'QR coupon', blurb: 'A big offer + a scannable QR code.', price: p.qrCoupon },
  ].filter((f) => typeof f.price === 'number' && (f.price as number) > 0);
}

export function mountAdvertise(mount: HTMLElement, cfg: EmbedConfig, _api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  // Back from the sign-in that "Create your ad" started: carry on to the builder (once).
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'advertise') {
    auth.clearPending();
    if (auth.isLoggedIn && cfg.advertisingEnabled && formats(cfg).length > 0 && !salesPausedMessage(cfg)) {
      goToBuilder(cfg, theme);
      return;
    }
  }
  // Only when ads are actually on sale: otherwise the "not accepting ads" message already says it.
  if (cfg.advertisingEnabled) showSalesPaused(root, cfg);

  // Returned from a successful submission on the 365 platform?
  const params = new URLSearchParams(location.search);
  const back = params.get('advertise');
  if (back === 'submitted' || back === 'updated') {
    track('advertise_success', 'advertise'); // funnel: before the flag is stripped below
    renderSubmitted(root, cfg, auth, theme, back === 'updated');
    // Strip the flag so a refresh shows the menu again.
    params.delete('advertise');
    const qs = params.toString();
    history.replaceState(null, '', location.pathname + (qs ? `?${qs}` : '') + location.hash);
    return;
  }

  // Not accepting ads online (not entitled / not accepting / no prices) → a soft note, never a blank box.
  if (!cfg.advertisingEnabled || formats(cfg).length === 0) {
    root.append(
      h('h3', undefined, 'Advertise on our screens'),
      h('div', { class: 'e365-alert info' },
        `${cfg.venueName} isn’t accepting ads online right now. Please check back soon or contact us to advertise here.`),
      footer(),
    );
    return;
  }

  renderMenu(root, cfg, auth, theme);
}

function goToBuilder(cfg: EmbedConfig, theme?: ThemeOptions): void {
  const ret = location.href.split('?')[0]; // strip any prior ?advertise=…
  // The builder wears the venue site's accent colour so it doesn't feel like leaving their site (hex only).
  const accent = theme?.primary && /^#[0-9a-f]{6}$/i.test(theme.primary) ? `&accent=${encodeURIComponent(theme.primary)}` : '';
  track('advertise_start', 'advertise');
  window.location.href = withWa(`${platformBase(cfg)}/create-ad?embedVenue=${encodeURIComponent(cfg.venueId)}&return=${encodeURIComponent(ret)}${accent}`);
}

function renderMenu(root: HTMLElement, cfg: EmbedConfig, auth: Auth, theme?: ThemeOptions): void {
  clear(root);
  root.append(h('h3', undefined, 'Advertise on our screens'));
  root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 12px' },
    `Reach everyone at ${cfg.venueName} — your ad rotates on our TVs alongside live scores. Fixed monthly price, cancel anytime.`));
  if (cfg.adScreensComingSoon) {
    root.append(h('div', { class: 'e365-alert info', style: 'margin-bottom:12px;' },
      'Our screens are being installed now. Create and send your ad today; you can start it once they’re live.'));
  }

  const list = h('div', { style: 'display:flex;flex-direction:column;gap:8px;margin-bottom:14px;' });
  for (const f of formats(cfg)) {
    const row = h('div', {
      style: 'display:flex;justify-content:space-between;align-items:center;gap:10px;border:1px solid var(--e365-border);border-radius:var(--e365-radius);padding:10px 12px;background:var(--e365-surface);',
    });
    const left = h('div');
    left.append(h('div', { style: 'font-weight:700;' }, f.label));
    left.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem;' }, f.blurb));
    const price = h('div', { style: 'font-weight:800;white-space:nowrap;color:var(--e365-primary);' }, `$${(f.price as number).toFixed(0)}/mo`);
    row.append(left, price);
    list.append(row);
  }
  root.append(list);

  const btn = h('button', { class: 'e365-btn' }, 'Create your ad') as HTMLButtonElement;
  btn.addEventListener('click', () => {
    if (blockIfSalesPaused(cfg, btn)) return;
    if (!auth.isLoggedIn) auth.redirectToLogin({ t: 'advertise' });
    else goToBuilder(cfg, theme);
  });
  root.append(btn);
  root.append(h('div', { class: 'e365-muted', style: 'font-size:.75rem;margin-top:8px;' },
    'You’ll sign in (or create a free account), upload your ad, and submit it. We review every ad before it goes live.'));
  root.append(footer());
}

function renderSubmitted(root: HTMLElement, cfg: EmbedConfig, auth: Auth, theme?: ThemeOptions, updated = false): void {
  clear(root);
  root.append(h('h3', undefined, 'Advertise on our screens'));
  root.append(h('div', { class: 'e365-alert info' }, updated
    ? `✓ Your changes were saved. ${cfg.venueName} will review the updated ad.`
    : `✓ Your ad was submitted to ${cfg.venueName}. We’ll review it and email you — once it’s approved you can start your subscription and go live.`));
  // Point at where the ad's status lives — the my-ads widget below on this page, or its page.
  root.append(hasMount('my-ads')
    ? h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin-top:8px;' }, 'Track its status under “My Ads” below.')
    : h('div', { class: 'e365-muted', style: 'font-size:.85rem; margin-top:8px;' }, 'Track its status under ',
        h('a', { href: sitePage('advertise') }, 'My Ads'), '.'));
  const again = h('button', { class: 'e365-btn' }, 'Create another ad') as HTMLButtonElement;
  again.addEventListener('click', () => renderMenu(root, cfg, auth, theme));
  root.append(again, footer());
}
